import { memo, useCallback, useEffect, useRef, useState } from 'react'

import usePermissions from '@/hooks/usePermissions'
import { Box, Button, Center, Grid, HStack, Icon, IconButton, Text } from '@chakra-ui/react'
import { ModalType, useGlobalModal } from '@infraloop/common'
import { useTranslation } from '@infraloop/i18n'
import { Project } from '@infraloop/services'
import { LuChevronRight, LuFileText, LuFocus, LuPencil, LuTrash2, LuUsers } from 'react-icons/lu'
import { useNavigate } from 'react-router'

import useInspectionAreas from '../../hooks/useInspectionAreas'
import useProjects from '../../hooks/useProjects'
import { PROJECT_GRID_COLUMNS } from '../../layout'
import {
  buildReportUrl,
  bytesToHumanReadable,
  formatDate,
  getProjectSize,
  getProjectViewerCount,
  parseDashboardHash,
} from '../../utils'
import InspectionAreaTable from './InspectionAreaTable'
import ProjectFormModal from './ProjectFormModal'
import RowActionsMenu, { RowAction } from './RowActionsMenu'
import ViewersModal from './ViewersModal'

/**
 * A single project row. Memoized so toggling table-level state (e.g. the
 * create-project modal) doesn't re-render every row in a large list.
 */
const ProjectRow = memo(
  ({
    project,
    isOwned,
  }: {
    /** Project to render as a row. */
    project: Project
    /** Whether this is the user's own project (enables modification actions). */
    isOwned: boolean
  }) => {
    // Hooks
    const { t } = useTranslation()
    const { showModal } = useGlobalModal()
    const { deleteProject } = useProjects()
    const { project: projectPermission } = usePermissions()
    const navigate = useNavigate()

    // Derived state — owning the project and holding write permission gates the mutating actions.
    const canModify = isOwned && projectPermission.update

    // States — start expanded when the dashboard was opened with this project's id in the URL
    // hash (e.g. the editor's back-to-dashboard button), so its area sub-table auto-expands.
    const { projectId: hashProjectId, areaId: hashAreaId } = parseDashboardHash(window.location.hash)
    const isHashTarget = hashProjectId === project.project_id
    const [isExpanded, setIsExpanded] = useState(isHashTarget)
    const [isViewersOpen, setIsViewersOpen] = useState(false)
    const [isEditOpen, setIsEditOpen] = useState(false)

    // Ref to the row, used to scroll the hash-targeted project into view on arrival.
    const rowRef = useRef<HTMLDivElement>(null)

    /**
     * When the dashboard is opened with only this project's id in the hash (no area), bring the
     * row into view once it mounts. When an area id is also present, the area sub-table scrolls
     * to and highlights that specific area instead — more precise on a long area list.
     */
    useEffect(() => {
      if (isHashTarget && !hashAreaId) {
        rowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }, [isHashTarget, hashAreaId])

    // Data — inspection areas load lazily once the row is expanded.
    const { areas, isLoadingAreas, createArea, updateArea, deleteArea } = useInspectionAreas(
      project.project_id,
      isExpanded,
    )

    // Derived state
    const viewerCount = getProjectViewerCount(project)

    /**
     * Open the viewers modal (viewer list; the invite form is revealed from within the modal).
     */
    const openViewers = useCallback(() => {
      setIsViewersOpen(true)
    }, [])

    /**
     * Open the edit-project modal, prefilled with the project.
     */
    const openEdit = useCallback(() => {
      setIsEditOpen(true)
    }, [])

    /**
     * Navigate to the project's report (帳票詳細) page.
     */
    const openReportDetail = useCallback(() => {
      void navigate(buildReportUrl(project.project_id))
    }, [navigate, project.project_id])

    /**
     * Ask the user to confirm deleting this project, then run the delete mutation. On
     * failure the mutation has toasted the error, so the dialog stays open for a retry.
     */
    const confirmDelete = useCallback(() => {
      showModal({
        modalType: ModalType.ConfirmationCritical,
        title: t('hatsuly.modal.project.delete.title'),
        body: t('hatsuly.modal.project.delete.body', { name: project.project_name }),
        confirmText: t('ui.delete'),
        onConfirmPromised: async () => {
          try {
            await deleteProject({ projectId: project.project_id })
            return true
          } catch {
            return false
          }
        },
      })
    }, [showModal, t, project.project_name, project.project_id, deleteProject])

    // Project kebab actions. Viewers (and admins in read-only mode) see only the report
    // entry; owners with write permission also get the mutating actions, in the mockup's order.
    const actions: RowAction[] = [
      ...(canModify
        ? [{ value: 'edit', label: t('hatsuly.menu.projectEdit'), icon: LuPencil, onSelect: openEdit }]
        : []),
      { value: 'report-detail', label: t('hatsuly.menu.reportDetail'), icon: LuFileText, onSelect: openReportDetail },
      ...(canModify
        ? [
            {
              value: 'manage-viewers',
              label: t('hatsuly.menu.manageViewers'),
              icon: LuUsers,
              onSelect: openViewers,
            },
            {
              value: 'delete',
              label: t('hatsuly.menu.projectDelete'),
              icon: LuTrash2,
              danger: true,
              separatorBefore: true,
              onSelect: confirmDelete,
            },
          ]
        : []),
    ]

    /**
     * Toggle whether the project's inspection-area sub-table is expanded.
     */
    const toggleExpanded = useCallback(() => {
      setIsExpanded((prev) => !prev)
    }, [])

    return (
      <Box ref={rowRef} borderBottom="1px solid" borderColor="gray.100">
        <Grid
          templateColumns={PROJECT_GRID_COLUMNS}
          alignItems="center"
          columnGap="8px"
          px="16px"
          py="14px"
          _hover={{ bg: '#F8FAFD' }}
        >
          <IconButton
            variant="ghost"
            size="xs"
            w="28px"
            h="28px"
            minW="28px"
            borderRadius="7px"
            color="gray.500"
            transition="transform .18s"
            transform={isExpanded ? 'rotate(90deg)' : 'rotate(0deg)'}
            onClick={toggleExpanded}
            aria-label={t('hatsuly.projectList.toggleAreas')}
            data-testid="project-expand"
          >
            <Icon as={LuChevronRight} boxSize="17px" />
          </IconButton>

          <HStack gap="11px" minW={0} overflow="hidden">
            <Center w="34px" h="34px" borderRadius="9px" bg="primary.50" color="primary.600" flexShrink={0}>
              <Icon as={LuFocus} boxSize="17px" />
            </Center>
            <Button
              variant="plain"
              h="auto"
              minW="0"
              maxW="100%"
              flexShrink={1}
              px="0"
              py="0"
              overflow="hidden"
              justifyContent="flex-start"
              _hover={{ color: 'primary.600' }}
              onClick={toggleExpanded}
              data-testid="project-name"
            >
              <Text
                as="span"
                display="block"
                minW={0}
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                fontSize="15px"
                fontWeight="semibold"
                color="gray.800"
              >
                {project.project_name}
              </Text>
            </Button>
          </HStack>

          <Box display="flex" justifyContent="center">
            <Button
              variant="outline"
              size="xs"
              h="auto"
              gap="6px"
              px="10px"
              py="4px"
              borderColor="gray.200"
              borderRadius="full"
              fontSize="12px"
              fontWeight="medium"
              color="gray.700"
              bg="white"
              _hover={{ bg: 'gray.100' }}
              onClick={openViewers}
              data-testid="project-viewers"
            >
              <Icon as={LuUsers} boxSize="13px" color="gray.400" />
              {viewerCount}
              {t('hatsuly.projectList.viewersUnit')}
            </Button>
          </Box>

          <Text fontSize="13px" color="gray.700" textAlign="right" pr="20px">
            {bytesToHumanReadable(getProjectSize(project))}
          </Text>

          <Text fontSize="13px" color="gray.700" textAlign="center">
            {formatDate(project.updated_at)}
          </Text>
          <Text fontSize="13px" color="gray.400" textAlign="center">
            {formatDate(project.created_at)}
          </Text>

          <Center>
            <RowActionsMenu items={actions} />
          </Center>
        </Grid>

        {isExpanded && (
          <InspectionAreaTable
            project={project}
            areas={areas}
            isLoading={isLoadingAreas}
            isOwned={isOwned}
            onCreateArea={createArea}
            onUpdateArea={updateArea}
            onDeleteArea={deleteArea}
          />
        )}

        {isViewersOpen && <ViewersModal project={project} onClose={() => setIsViewersOpen(false)} />}

        {isEditOpen && <ProjectFormModal isOpen project={project} onClose={() => setIsEditOpen(false)} />}
      </Box>
    )
  },
)

ProjectRow.displayName = 'ProjectRow'

export default ProjectRow
